import { dayjs } from '#gp/app-common/util/dayjs';
import { gpCommon } from '../app';
import { ISimpleHttpService } from './authorized-http-service';
import { guessSimpleTimezone } from '../util/simple-timezones';
import { getLogger } from '../util/logger';


export interface IProject {
    id: string | null;
    name: string;
    description: string | null;
    lastEditedUTC: dayjs.Dayjs | null;
    lastEditedUserEmail: string;
    isShared: boolean;
    accessLevel: string;
    thumbnailUrl: string;
    planStartUTC: dayjs.Dayjs | null;
    planEndUTC: dayjs.Dayjs | null;
    workHours: number;
    percentComplete: number;
    subprojectCount: number;
    ownerEmail: string;
}

export interface IProjectInfo {
    name: string;
    description: string;
}

export class ProjectServiceMessages {
    public static UNAUTHORIZED = "__unauthorized__";
}

export interface AddProjectOptions {
    startTutorial?: boolean;
    startWizard?: boolean;
}

// The plain list getters swallow HTTP failures into an empty array, so an
// outage is indistinguishable from an empty account. Callers that must make
// that distinction (first-plan auto-start, empty states that assert facts)
// use the -Checked variants and read `succeeded`.
export interface IProjectListFetchResult {
    projects: IProject[];
    succeeded: boolean;
}

export interface IProjectService {
    addProject(name: string, templateId: string | null, options: AddProjectOptions): ng.IPromise<IProject>;
    deleteProjectById(projectId: string): ng.IPromise<boolean>;
    undeleteProjectById(projectId: string): ng.IPromise<IProject>;
    getProjects(): ng.IPromise<IProject[]>;
    getDeletedProjects(): ng.IPromise<IProject[]>;
    getSharedProjects(): ng.IPromise<IProject[]>;
    getTemplates(): ng.IPromise<IProject[]>;
    getProjectsChecked(): ng.IPromise<IProjectListFetchResult>;
    getDeletedProjectsChecked(): ng.IPromise<IProjectListFetchResult>;
    getSharedProjectsChecked(): ng.IPromise<IProjectListFetchResult>;
    getTemplatesChecked(): ng.IPromise<IProjectListFetchResult>;
    deleteTemplateById(projectId: string): ng.IPromise<boolean>;
    saveProjectInfo(projectId: string, info: IProjectInfo): ng.IPromise<boolean>;
    saveProjectAsTemplate(projectId: string, templateName: string): ng.IPromise<any>;
    saveProjectAsNewPlan(projectId: string, newPlanName: string): ng.IPromise<IProject>;
}

export type gpProjectsService = IProjectService;

interface ProjectReturnModel {
    id: string;
    name: string;
    description: string;
    lastEditedUTC: string;
    lastEditedUserEmail: string;
    accessLevel: string;
    isShared: boolean;

    thumbnailUrl: string;
    planStartUTC: string;
    planEndUTC: string;
    workHours: number;
    percentComplete: number;
    subprojectCount: number;
    ownerEmail: string;
}

const logger = getLogger('workspace');

export const gpProjectsService_ServiceName = 'projectsService';

gpCommon.factory(gpProjectsService_ServiceName, ['$httpAuth', '$q', function ($http: ISimpleHttpService, $q: ng.IQService) {

    const serviceBase = __gameplan_service_url || 'http://localhost/';
    return new ProjectsService(serviceBase, $http, $q);
}]);


class GetProjectOptions {
    deleted: boolean;
    shared: boolean;
}

class ProjectsService implements IProjectService {

    constructor(
        public serviceBase: string,
        public $http: ISimpleHttpService,
        public $q: ng.IQService) {
    }

    public getProjects(): ng.IPromise<IProject[]> {
        return this.getProjectsOrTemplates("projects", { deleted: false, shared: false });
    }

    public getDeletedProjects(): ng.IPromise<IProject[]> {
        return this.getProjectsOrTemplates("projects", { deleted: true, shared: false });
    }

    public getSharedProjects(): ng.IPromise<IProject[]> {
        return this.getProjectsOrTemplates("projects", { deleted: false, shared: true });
    }

    public getTemplates(): ng.IPromise<IProject[]> {
        return this.getProjectsOrTemplates("templates", { deleted: false, shared: false });
    }

    public getProjectsChecked(): ng.IPromise<IProjectListFetchResult> {
        return this.getProjectsOrTemplatesChecked("projects", { deleted: false, shared: false });
    }

    public getDeletedProjectsChecked(): ng.IPromise<IProjectListFetchResult> {
        return this.getProjectsOrTemplatesChecked("projects", { deleted: true, shared: false });
    }

    public getSharedProjectsChecked(): ng.IPromise<IProjectListFetchResult> {
        return this.getProjectsOrTemplatesChecked("projects", { deleted: false, shared: true });
    }

    public getTemplatesChecked(): ng.IPromise<IProjectListFetchResult> {
        return this.getProjectsOrTemplatesChecked("templates", { deleted: false, shared: false });
    }

    public deleteProjectById(projectId: string): ng.IPromise<boolean> {
        return this.deleteProjectOrTemplateById('projects', projectId);
    }

    public undeleteProjectById(projectId: string): ng.IPromise<IProject> {
        return this.undeleteProjectOrTemplateById('projects', projectId);
    }

    public deleteTemplateById(projectId: string): ng.IPromise<boolean> {
        return this.deleteProjectOrTemplateById('templates', projectId);
    }

    private getProjectsOrTemplates(type: string, options: GetProjectOptions): ng.IPromise<IProject[]> {
        return this.getProjectsOrTemplatesChecked(type, options).then(result => result.projects);
    }

    private getProjectsOrTemplatesChecked(type: string, options: GetProjectOptions): ng.IPromise<IProjectListFetchResult> {

        var me = this;
        var $http = this.$http;
        var $q = this.$q;

        var deferred = $q.defer<IProjectListFetchResult>();

        options = options || { deleted: false, shared: false, };

        logger.debug("getProjectsOrTemplates()");
        $http.getValue<ProjectReturnModel[]>(me.serviceBase + 'api/gameplan/' + type, { params: { deleted: options.deleted, shared: options.shared } })
            .then(data => {
                logger.debug("getProjectsOrTemplates() - data =" + JSON.stringify(data));
                var result = data.map((r: ProjectReturnModel) => this.toProject(r));
                deferred.resolve({ projects: result, succeeded: true });

            }).catch(reason => {
                logger.debug("getProjectsOrTemplates() - error =" + JSON.stringify(reason));
                deferred.resolve({ projects: [], succeeded: false });
            });

        return deferred.promise;
    }

    public deleteProjectOrTemplateById(type: string, projectId: string): ng.IPromise<boolean> {
        var me = this;
        var $http = this.$http;
        var $q = this.$q;

        var deferred = $q.defer<boolean>();

        $http.delete(me.serviceBase + 'api/gameplan/' + type + '/' + projectId)
            .then(response => {

                var result = response;

                deferred.resolve(true);

            }).catch(reason => {
                deferred.resolve(false);
            });

        return deferred.promise;
    }

    public undeleteProjectOrTemplateById(type: string, projectId: string): ng.IPromise<IProject> {
        var $http = this.$http;
        var $q = this.$q;

        var deferred = $q.defer<IProject>();

        var req = $http.post<ProjectReturnModel>(this.serviceBase + 'api/gameplan/' + type + '/' + projectId, {
            undelete: true,
        })

        req.then(response => {
            if (response.data) {
                var result = this.toProject(response.data);
                deferred.resolve(result);
            } else {
                deferred.reject("no data");
            }
        }).catch(err => { });

        req.catch(reason => deferred.reject(reason));

        return deferred.promise;
    }

    public addProject(name: string, templateId: string, options: AddProjectOptions): ng.IPromise<IProject> {

        var me = this;
        var $http = this.$http;
        var $q = this.$q;

        var params = {
            projectName: name,
            templateId: templateId,
            useTutorial: options ? !!options.startTutorial : false,
            useWizard: options ? !!options.startWizard : false,
            timezone: guessSimpleTimezone(),
        };

        var deferred = $q.defer<IProject>();

        var req = $http.post<ProjectReturnModel>(me.serviceBase + 'api/gameplan/projects', params)

        req.then(response => {
            if (response.data) {
                var result = this.toProject(response.data);
                deferred.resolve(result);
            }
            else {
                deferred.reject("No data");
            }
        }).catch(err => { });

        req.catch(reason => {
            deferred.reject(reason);
        });

        return deferred.promise;
    }

    public saveProjectInfo(projectId: string, info: IProjectInfo): ng.IPromise<boolean> {
        let me = this;

        let deferred = this.$q.defer<boolean>();

        // crete a new object to send to the web api because we don't want any accidental fields making their way to the server.
        let params = {
            name: info.name,
            description: info.description
        };

        let req = this.$http.put(me.serviceBase + 'api/gameplan/projects/' + projectId, params);

        req.then(response => deferred.resolve(true));
        req.catch(reason => {
            deferred.reject(reason);
        }).catch(err => { });

        return deferred.promise;
    }

    saveProjectAsTemplate(projectId: string, templateName: string): ng.IPromise<any> {

        const deferred = this.$q.defer<any>();

        const templateInfo = {
            projectId: projectId,
            templateName: templateName,
        };

        // we do a nothing transform response here because we want raw string data, not an object.
        this.$http.post<string>(this.serviceBase + 'api/gameplan/templates', templateInfo).then(response => deferred.resolve(response.data))
            .catch(response => {
                logger.error("Error saving", response.status, response);
                deferred.reject(response.status + " " + response.statusText);
            });

        return deferred.promise;
    }

    saveProjectAsNewPlan(projectId: string, newPlanName: string): ng.IPromise<IProject> {

        const deferred = this.$q.defer<any>();

        const newPlanInfo = {
            projectName: newPlanName,
            templateId: projectId,
            copyProject: true,
            clearNotifications: true,
        }

        // we do a nothing transform response here because we want raw string data, not an object.
        this.$http.post<string>(this.serviceBase + 'api/gameplan/projects', newPlanInfo).then(response => deferred.resolve(response.data))
            .catch(response => {
                logger.error("Error saving", response.status, response);
                deferred.reject(response.status + " " + response.statusText);
            });

        return deferred.promise;
    }

    private toProject(r: ProjectReturnModel): IProject {
        return {
            id: r.id,
            description: r.description,
            name: r.name,
            lastEditedUTC: dayjs.utc(r.lastEditedUTC),
            lastEditedUserEmail: r.lastEditedUserEmail,
            accessLevel: r.accessLevel,
            isShared: r.isShared,
            thumbnailUrl: r.thumbnailUrl,
            planStartUTC: toDayjsUtc(r.planStartUTC),
            planEndUTC: toDayjsUtc(r.planEndUTC),
            workHours: r.workHours,
            percentComplete: r.percentComplete,
            subprojectCount: r.subprojectCount,
            ownerEmail: r.ownerEmail,
        };
    }
}

function toDayjsUtc(text: string): dayjs.Dayjs | null {
    if (text && text !== "") {
        return dayjs.utc(text);
    } else {
        return null;
    }
}
